import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Search, Check, Plus, X } from "lucide-react";

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.FC<any>;
  description?: string;
  disabled?: boolean;
}

interface SearchableSelectProps {
  options: SelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  required?: boolean;
  error?: string;
  icon?: React.FC<any>;
  addNew?: {
    label: string;
    onAdd: () => void;
  };
  loading?: boolean;
  onSearch?: (query: string) => void;
  disabled?: boolean;
  className?: string;
  clearable?: boolean;
  variant?: "default" | "success";
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  required,
  error,
  icon: TriggerIcon,
  addNew,
  loading,
  onSearch,
  disabled,
  className = "",
  clearable = false,
  variant = "default",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.description?.toLowerCase().includes(q)
    );
  }, [options, search]);

  const positionDropdown = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 320;
    const openAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    setDropdownStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  useEffect(() => {
    if (open) {
      positionDropdown();
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setSearch("");
      setHighlightIndex(-1);
    }
  }, [open, positionDropdown]);

  useEffect(() => {
    if (!open) return;
    const handleScroll = () => positionDropdown();
    const handleResize = () => positionDropdown();
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [open, positionDropdown]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      )
        return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (onSearch) onSearch(search);
  }, [search, onSearch]);

  const handleSelect = (opt: SelectOption) => {
    if (opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(filtered[highlightIndex]);
    }
  };

  const hasError = !!error;

  return (
    <div className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={placeholder}
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        onKeyDown={handleKeyDown}
        className={`w-full flex items-center gap-2 rounded-xl px-4 py-3 text-sm text-left transition-all ring-2 ${
          hasError
            ? "ring-red-300 focus:ring-red-500"
            : open
            ? "ring-brand-accent"
            : variant === "success"
            ? "ring-emerald-400 hover:ring-emerald-500 focus:ring-emerald-500"
            : "ring-slate-300 hover:ring-slate-400 focus:ring-brand-accent"
        } ${disabled ? "bg-slate-50 cursor-not-allowed opacity-60" : "bg-white cursor-pointer"}`}
      >
        {TriggerIcon && (
          <TriggerIcon className="h-4 w-4 text-slate-400 flex-shrink-0" />
        )}
        <span
          className={`flex-1 truncate ${
            selectedOption ? "text-slate-800" : "text-slate-400"
          }`}
        >
          {selectedOption?.label || placeholder}
        </span>
        {clearable && value && !disabled && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            className="p-0.5 rounded hover:bg-slate-100 transition-colors"
          >
            <X className="h-3.5 w-3.5 text-slate-400" />
          </span>
        )}
        <ChevronDown
          className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Error text */}
      {error && (
        <p className="mt-1.5 text-xs text-red-500">{error}</p>
      )}

      {/* Dropdown via portal */}
      {open &&
        createPortal(
          <AnimatePresence>
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.12 }}
              style={dropdownStyle}
              className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden"
            >
              {/* Add New */}
              {addNew && (
                <div className="px-3 pt-2 pb-1.5 border-b border-slate-100">
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      addNew.onAdd();
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg text-brand-accent hover:bg-emerald-50 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {addNew.label}
                  </button>
                </div>
              )}

              {/* Search input */}
              <div className="px-3 py-2.5 border-b border-slate-100">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={searchPlaceholder}
                    className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:border-emerald-600 bg-white"
                  />
                </div>
              </div>

              {/* Options list */}
              <div className="max-h-56 overflow-y-auto py-1" role="listbox">
                {loading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-6 text-sm text-slate-400">
                    No options found
                  </div>
                ) : (
                  filtered.map((opt, i) => {
                    const isSelected = opt.value === value;
                    const isHighlighted = i === highlightIndex;
                    const OptIcon = opt.icon;
                    return (
                      <div
                        key={opt.value}
                        role="option"
                        aria-selected={isSelected}
                        aria-disabled={opt.disabled}
                        onClick={() => handleSelect(opt)}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                          opt.disabled
                            ? "opacity-40 cursor-not-allowed"
                            : isHighlighted
                            ? "bg-slate-50"
                            : isSelected
                            ? "bg-brand-light"
                            : "hover:bg-slate-50"
                        }`}
                        onMouseEnter={() => setHighlightIndex(i)}
                      >
                        {OptIcon && (
                          <OptIcon
                            className={`h-4 w-4 flex-shrink-0 ${
                              isSelected ? "text-brand" : "text-slate-400"
                            }`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <span
                            className={`block truncate ${
                              isSelected
                                ? "text-brand font-medium"
                                : "text-slate-700"
                            }`}
                          >
                            {opt.label}
                          </span>
                          {opt.description && (
                            <span className="block text-xs text-slate-400 truncate">
                              {opt.description}
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <Check className="h-4 w-4 text-brand-accent flex-shrink-0" />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default SearchableSelect;
